import { LazyImage } from '../ui/LazyImage'
import { useTmdbLazyMovie, useTmdbLazySeries } from '../library/useTmdbLazy'
import type { TmdbSimilarTitle } from '../tmdb/tmdb'

/**
 * Poster-style tile used by the home rows. Two variants:
 *
 *   - `FromTmdbTitle` — for TMDB-sourced rows (Recommended, Trending)
 *     where we already have a lean `TmdbSimilarTitle` payload.
 *   - `FromLibraryEntry` — for library-sourced rows (Recently Rated,
 *     Recently Added, Top Rated by You) where we only know the tmdbId
 *     and need to lazy-fetch TMDB details.
 *
 * Both visually mirror `DiscoverCard` (gradient title overlay, gold
 * year, gold user-rating badge, blue TMDB badge) so every home row
 * looks identical to the Discover tab rows. The difference is the
 * library variant shows the user's own rating in gold when set,
 * matching the desktop MovieCardView/CarouselView convention.
 */

interface FromTmdbTitleProps {
  item: TmdbSimilarTitle
  userRating: number | null
  onClick: () => void
}

export function HomeTmdbPosterCard({
  item,
  userRating,
  onClick,
}: FromTmdbTitleProps) {
  return (
    <PosterShell
      title={item.title}
      year={item.year}
      posterUrl={item.posterUrl}
      userRating={userRating}
      tmdbRating={item.rating}
      loading={false}
      onClick={onClick}
      ariaLabel={
        item.year != null
          ? `${item.title} (${item.year}), TMDB rating ${item.rating.toFixed(1)}`
          : `${item.title}, TMDB rating ${item.rating.toFixed(1)}`
      }
    />
  )
}

interface FromLibraryMovieProps {
  tmdbId: number
  userRating: number | null
  onClick: () => void
}

/**
 * Library movie tile. Wraps `useTmdbLazyMovie` so the card only fetches
 * TMDB once it scrolls into view. Falls back to a poster placeholder
 * while the details resolve.
 */
export function HomeLibraryMovieCard({
  tmdbId,
  userRating,
  onClick,
}: FromLibraryMovieProps) {
  const { details, loading, ref } = useTmdbLazyMovie(tmdbId)
  return (
    <PosterShell
      refCallback={ref}
      title={details?.title ?? `TMDB #${tmdbId}`}
      year={details?.year ?? null}
      posterUrl={details?.posterUrl ?? null}
      userRating={userRating}
      tmdbRating={details?.rating ?? 0}
      loading={loading || !details}
      onClick={onClick}
      ariaLabel={
        details?.title
          ? `${details.title}${details.year ? ` (${details.year})` : ''}`
          : `Movie ${tmdbId}`
      }
    />
  )
}

interface FromLibrarySeriesProps {
  tmdbId: number
  userRating: number | null
  onClick: () => void
}

/**
 * Library series tile. Parallels `HomeLibraryMovieCard` but uses the
 * series lazy-fetch hook.
 */
export function HomeLibrarySeriesCard({
  tmdbId,
  userRating,
  onClick,
}: FromLibrarySeriesProps) {
  const { details, loading, ref } = useTmdbLazySeries(tmdbId)
  return (
    <PosterShell
      refCallback={ref}
      title={details?.name ?? `TMDB #${tmdbId}`}
      year={details?.year ?? null}
      posterUrl={details?.posterUrl ?? null}
      userRating={userRating}
      tmdbRating={details?.rating ?? 0}
      loading={loading || !details}
      onClick={onClick}
      ariaLabel={
        details?.name
          ? `${details.name}${details.year ? ` (${details.year})` : ''}`
          : `Series ${tmdbId}`
      }
    />
  )
}

function PosterShell({
  refCallback,
  title,
  year,
  posterUrl,
  userRating,
  tmdbRating,
  loading,
  onClick,
  ariaLabel,
}: {
  refCallback?: (el: HTMLElement | null) => void
  title: string
  year: number | null
  posterUrl: string | null
  userRating: number | null
  tmdbRating: number
  loading: boolean
  onClick: () => void
  ariaLabel: string
}) {
  return (
    <div
      ref={refCallback as ((el: HTMLDivElement | null) => void) | undefined}
      className="shrink-0 snap-start"
      style={{ width: 'clamp(120px, 16vw, 180px)' }}
    >
      <button
        type="button"
        onClick={onClick}
        className="group flex w-full flex-col text-left focus:outline-none"
        aria-label={ariaLabel}
      >
        <div className="relative aspect-[2/3] w-full overflow-hidden rounded-lg border border-border-primary bg-gradient-to-br from-bg-secondary to-bg-tertiary shadow-cinematic transition-all duration-300 ease-cinematic group-hover:scale-[1.03] group-hover:border-accent-primary/40 group-focus-visible:ring-2 group-focus-visible:ring-accent-primary/60">
          {posterUrl ? (
            <LazyImage
              src={posterUrl}
              alt={title}
              className="h-full w-full object-cover"
            />
          ) : loading ? (
            <div className="absolute inset-0 animate-pulse bg-gradient-to-br from-bg-secondary to-bg-tertiary" />
          ) : (
            <div className="grid h-full w-full place-items-center px-2 text-center text-[11px] text-text-tertiary">
              {title}
            </div>
          )}

          {userRating != null ? (
            <Badge className="bg-accent-gold/80 text-black">
              <Star /> {userRating}
            </Badge>
          ) : tmdbRating > 0 ? (
            <Badge className="bg-blue-500/80 text-white">
              <Star /> {tmdbRating.toFixed(1)}
            </Badge>
          ) : null}

          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-2 pt-8">
            <p className="line-clamp-2 text-xs font-medium leading-tight text-white drop-shadow-lg sm:text-sm">
              {title}
            </p>
            {year != null && (
              <p className="mt-0.5 text-[10px] font-medium text-accent-gold drop-shadow sm:text-[11px]">
                {year}
              </p>
            )}
          </div>
        </div>
      </button>
    </div>
  )
}

function Badge({
  children,
  className,
}: {
  children: React.ReactNode
  className: string
}) {
  return (
    <div
      className={`absolute left-1.5 top-1.5 flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-semibold shadow-sm backdrop-blur-sm sm:text-[11px] ${className}`}
    >
      {children}
    </div>
  )
}

function Star() {
  return <span className="opacity-75">★</span>
}
